import React, { FC, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { WorkflowCard, WorkflowCardVariant } from 'components/molecules/Generic/WorkflowCard';
import { ICONS, resolveWorkflowNavigationEndpoint } from 'utils/ppaHelpers';
import {
    ppaWorkflowBtnTestId,
    ppaWorkflowCardTestId,
    ppaWorkflowIconTestId,
    ppaWorkflowPointTestId,
    ppaWorkflowTitleTestId,
    slugifyTestIdSegment,
} from 'constants/ppaTestIds';

export interface WorkflowCardData {
    workflow_id?: string;
    title: string;
    action_button?: {
        icon?: string;
        label?: string;
        iconVariant?: WorkflowCardVariant;
        iconColor?: string;
        endpoint?: string;
        isDisabled?: boolean;
    },
    description_points: string[];
}

interface WorkflowCardsGridProps {
    cards: WorkflowCardData[];
    style?: React.CSSProperties;
    className?: string;
    'data-testid'?: string;
}

type WorkflowCardItemProps = {
    card: WorkflowCardData;
    index: number;
};

const WorkflowCardItem: FC<WorkflowCardItemProps> = ({ card, index }) => {
    const navigate = useNavigate();
    const actionButton = card.action_button;
    const endpoint = actionButton
        ? resolveWorkflowNavigationEndpoint(card) ?? actionButton.endpoint
        : undefined;
    const iconName = actionButton?.icon;

    const workflowKey = useMemo(
        () => card.workflow_id ?? slugifyTestIdSegment(card.title) ?? String(index),
        [card.workflow_id, card.title, index],
    );

    const testIds = useMemo(
        () => ({
            card: ppaWorkflowCardTestId(workflowKey),
            title: ppaWorkflowTitleTestId(workflowKey),
            icon: ppaWorkflowIconTestId(workflowKey),
            button: ppaWorkflowBtnTestId(workflowKey),
        }),
        [workflowKey],
    );

    const getPointTestId = useCallback(
        (pointIndex: number) => ppaWorkflowPointTestId(workflowKey, pointIndex),
        [workflowKey],
    );

    const onButtonClick = useCallback(() => {
        if (!endpoint) return;
        navigate(endpoint.startsWith('/') ? endpoint : `/${endpoint}`);
    }, [endpoint, navigate]);

    const icon = useMemo(() => {
        if (!iconName || !ICONS[iconName]) return undefined;
        return React.createElement(ICONS[iconName], { color: actionButton?.iconColor });
    }, [iconName, actionButton?.iconColor]);

    return (
        <div className="min-w-0">
            <WorkflowCard
                title={card.title}
                descriptionPoints={card.description_points}
                buttonLabel={actionButton?.label ?? ''}
                onButtonClick={endpoint ? onButtonClick : undefined}
                icon={icon}
                iconVariant={actionButton?.iconVariant ?? 'warning'}
                isDisabled={actionButton?.isDisabled ?? false}
                data-testid={testIds.card}
                titleTestId={testIds.title}
                iconTestId={testIds.icon}
                getPointTestId={getPointTestId}
                buttonTestId={testIds.button}
            />
        </div>
    );
};

export const WorkflowCardsGrid: FC<WorkflowCardsGridProps> = ({
    cards,
    style,
    className = '',
    'data-testid': testId,
}) => (
    <div
        className={`grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 ${className}`}
        style={style}
        data-testid={testId}
    >
        {cards.map((card, i) => (
            <WorkflowCardItem key={card.workflow_id || i} card={card} index={i} />
        ))}
    </div>
);

export default WorkflowCardsGrid;
